// "Open in ChatGPT" prompts. The transcript itself is built server-side
// (Conversations::MhChatTranscriptService) and already starts with a legend:
// C = customer, US = our staff, NOTE = internal note, [auto-reply].

// chatgpt.com/?q= pre-fills (and sends) the prompt. Longer chats would
// exceed safe URL length, so they open a blank chat for a manual paste.
const MAX_PREFILL_URL_LENGTH = 6000;
const CHATGPT_URL = 'https://chatgpt.com/';

const MK_SUMMARY_PROMPT =
  'Summarize this customer service chat in a few short points: ' +
  'what the customer wants, what we answered or promised, ' +
  'and what is still open / the next step.';

// Smith3D prompts, worded by the owner (2026-10-06) — keep verbatim.
const S3_SUMMARIZE_PROMPT = `You are helping Smith3D (3D printer shop, Kuala Lumpur) staff. C = customer, US = our staff, [auto-reply] = automatic.
In max 6 short bullet points:
- What the customer wants (latest request first)
- What we already answered, quoted (prices, deposits, invoice/quotation numbers) or promised
- What is still open, and who owes the next step (us or the customer)
- Anything we promised that looks forgotten (e.g. "will update you" with no update after)
Use only what is in the chat. If something is unclear, say "unclear" - do not guess.`;

const S3_DRAFT_PROMPT = `Draft our next WhatsApp reply to the customer's latest message(s).
- Same language the customer uses (English / Malay / Chinese), short, friendly, max 4 lines, no long dashes.
- Answer only what was asked. Do NOT state any price, stock, delivery date, discount or refund unless it is already written by US in this chat.
- Never say a payment is received or confirmed.
- If the answer needs information we don't have, write the reply as "let me check and update you" and list underneath what staff need to find out.
Give the reply, then one line: "Check before sending: ..."`;

const S3_RED_FLAGS_PROMPT = `Check this chat for anything abnormal before staff act on it. List only what you actually see, with the message time:
- Payment red flags: asks to pay a different account/person, overpayment + refund the difference, slip amount/name not matching, pressure to release goods before payment clears
- Identity red flags: claims to be government/company buyer but uses a non-official email (not .gov.my / company domain), "our email is down, use this one", asks for our SSM / bank statement / EFT form before any order is confirmed
- Pressure: "must deliver today", "boss will cancel", repeated chasing with new conditions
- Contradictions: different names, addresses or phone numbers; order details changing
- Promises WE made that we have not kept, and customers who sound upset
End with: "Risk: low / medium / high" and one line on what staff should do. If nothing is abnormal, say so in one line.`;

// All three in one ask: summary and red flags first, so the draft reply can
// take the risk into account.
const S3_ALL_PROMPT = `Do all three of the tasks below for this chat, under these headings.

## 1. Summary
${S3_SUMMARIZE_PROMPT}

## 2. Red flags
${S3_RED_FLAGS_PROMPT}

## 3. Draft reply
${S3_DRAFT_PROMPT}
If the risk in section 2 is medium or high, the draft must not move the deal forward (no confirming, releasing goods or sharing new payment details) until staff have checked.`;

// Each action is one button in the dialog; labelKey is an i18n key. The
// first action is the main (primary) button.
export const chatGptActions = isS3 =>
  isS3
    ? [
        { key: 'all', labelKey: 'ALL', prompt: S3_ALL_PROMPT },
        {
          key: 'summarize',
          labelKey: 'SUMMARIZE',
          prompt: S3_SUMMARIZE_PROMPT,
        },
        { key: 'draft', labelKey: 'DRAFT_REPLY', prompt: S3_DRAFT_PROMPT },
        { key: 'redFlags', labelKey: 'RED_FLAGS', prompt: S3_RED_FLAGS_PROMPT },
      ]
    : [{ key: 'summarize', labelKey: 'SUMMARIZE', prompt: MK_SUMMARY_PROMPT }];

export const chatGptUrl = text => {
  const prefilled = `${CHATGPT_URL}?q=${encodeURIComponent(text)}`;
  return prefilled.length <= MAX_PREFILL_URL_LENGTH
    ? { url: prefilled, prefilled: true }
    : { url: CHATGPT_URL, prefilled: false };
};
